import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { Formik } from 'formik';
import { CardElement } from 'react-stripe-elements';

import {
  STRIPE_OBJECT_TYPE_SOURCE,
  STRIPE_OBJECT_TYPE_TOKEN,
  STRIPE_SOURCE_TYPE_CARD,
} from 'common/stripeConstants';

import log from 'client/lib/log';
import withUtilityStripe from 'client/helpers/withUtilityStripe';

import SrText from 'client/components/SrText';
import SrButton, { SR_BUTTON_THEME_ADOBE_GREEN } from 'client/components/SrButton/SrButton';
import SrFormikField, { SR_FIELD_THEME_ADOBE } from 'client/components/SrFormikField';
import {
  createFormFieldsByIdGetter,
  createFormValidationFn,
  createInitialValues,
} from 'client/helpers/formikHelpers';
import SrBox from 'client/components/SrBox';

const debug = log.debug('utilityProfit:components:UPStripeCardForm');

const formFields = [
  {
    id: 'cardHolderName',
    title: 'Card Holder Name',
    type: 'short_text',
    properties: {
      placeholder: 'Write card holder name here...',
    },
    validations: ['required'],
  },
];

@withUtilityStripe
class UPStripeCardForm extends Component {
  static propTypes = {
    disabled: PropTypes.bool,
    onSubmit: PropTypes.func.isRequired,
    onError: PropTypes.func,
    submitErrorMessage: PropTypes.string,
    isSubmitting: PropTypes.bool,
    stripe: PropTypes.object.isRequired,
    buttonContainerClassName: PropTypes.string,
    buttonTitle: PropTypes.string,
    objectType: PropTypes.oneOf([STRIPE_OBJECT_TYPE_SOURCE, STRIPE_OBJECT_TYPE_TOKEN]),
    buttonHidden: PropTypes.bool,
    onReady: PropTypes.func,
  };

  static defaultProps = {
    buttonContainerClassName: '',
    buttonTitle: 'checkout',
    objectType: STRIPE_OBJECT_TYPE_SOURCE,
    buttonHidden: false,
  };

  constructor(props) {
    super(props);
    this.state = {
      isCreatingStripeSource: false,
      createStripeSourceError: null,
    };
    this._isUnmounting = false;
  }

  componentWillUnmount() {
    this._isUnmounting = true;
  }

  _onStripeReady = ({ formValues }) => {
    const { onReady } = this.props;
    if (onReady) {
      onReady(() => this._onSubmit({ formValues }));
    }
  };

  _onSubmit = ({ formValues }) => {
    const { onSubmit, onError, objectType, stripe } = this.props;

    debug(`Creating Stripe ${objectType}.`);

    this.setState({
      isCreatingStripeSource: true,
      createStripeSourceError: null,
    });

    if (!formValues.cardHolderName) {
      const error = new Error('Card holder name is required.');
      error.name = 'SrStripeCardHolderNameRequiredError';
      this.setState({
        isCreatingStripeSource: false,
        createStripeSourceError: error,
      });
      if (onError) {
        onError(error);
      }
      return;
    }

    const createStripeObjectPromise =
      objectType === STRIPE_OBJECT_TYPE_SOURCE
        ? stripe.createSource({
            type: STRIPE_SOURCE_TYPE_CARD,
            currency: 'USD',
            owner: {
              name: formValues.cardHolderName,
            },
          })
        : stripe.createToken({
            name: formValues.cardHolderName,
            currency: 'USD',
          });

    createStripeObjectPromise
      .then((result) => {
        let sourceOrToken;
        let error;

        if (objectType === STRIPE_OBJECT_TYPE_SOURCE) {
          sourceOrToken = result.source;
          error = result.error;
        } else {
          sourceOrToken = result.token;
          error = result.error;
        }

        if (!this._isUnmounting) {
          this.setState({
            isCreatingStripeSource: false,
            createStripeSourceError: null,
          });
        }
        if (error) {
          throw error;
        }

        if (!sourceOrToken) {
          const error = new Error(`stripe create ${objectType}: No response.`);
          error.name = 'SrStripeCreateSourceOrTokenNoResponseError';
          throw error;
        }

        debug(`Stripe ${objectType} received.`, sourceOrToken);
        onSubmit(sourceOrToken);
      })
      .catch((error) => {
        error.details = {
          ...(error.details || {}),
          formValues,
        };
        log.error(error);
        this.setState({
          isCreatingStripeSource: false,
          createStripeSourceError: error,
        });
        if (onError) {
          onError(error);
        }
      });
  };

  render() {
    const {
      disabled,
      isSubmitting,
      submitErrorMessage,
      buttonContainerClassName,
      buttonTitle,
      buttonHidden,
    } = this.props;

    const { isCreatingStripeSource, createStripeSourceError } = this.state;
    const isBusy = !!(isSubmitting || isCreatingStripeSource);

    debug({
      submitErrorMessage,
      createStripeSourceError,
    });

    const errorMessage = createStripeSourceError
      ? createStripeSourceError.message || JSON.stringify(createStripeSourceError)
      : submitErrorMessage || null;

    return (
      <div className="fieldsContainer">
        <Formik
          initialValues={createInitialValues({ formFields })}
          validate={createFormValidationFn(formFields)}
          render={(formikBag) => {
            const getFormFieldsById = createFormFieldsByIdGetter(formFields);
            return (
              <>
                <div className="cardElementContainer">
                  <div>
                    <SrFormikField
                      formField={getFormFieldsById('cardHolderName')}
                      formikBag={formikBag}
                      theme={SR_FIELD_THEME_ADOBE}
                    />
                    <SrBox marginTop="20px" marginBottom="-8px">
                      <SrText
                        font="greycliff"
                        weight="800"
                        lineHeight="25px"
                        margin="0 7px 0"
                        size="21"
                      >
                        Card Info
                      </SrText>
                    </SrBox>
                    <CardElement
                      disabled={disabled}
                      style={{
                        base: {
                          paddingLeft: '18px',
                          height: '60px',
                          width: '100%',
                          fontSize: '16px',
                          color: '#193340',
                        },
                      }}
                      onReady={() => this._onStripeReady({ formValues: formikBag.values })}
                    />
                  </div>
                  {errorMessage && (
                    <div className="formMessage error">
                      <span>{errorMessage}</span>
                    </div>
                  )}
                </div>
                {!buttonHidden && (
                  <div className={buttonContainerClassName}>
                    <SrButton
                      disabled={disabled || isBusy || !formikBag.isValid}
                      type="button"
                      onClick={() => this._onSubmit({ formValues: formikBag.values })}
                      theme={SR_BUTTON_THEME_ADOBE_GREEN}
                    >
                      {isBusy ? 'loading...' : buttonTitle}
                    </SrButton>
                    <span className="helperTextContainer">
                      <span className="helperText">
                        <span role="img" aria-label="lock">
                          {' '}
                          🔒
                        </span>{' '}
                        Securely encrypted payment
                      </span>
                    </span>
                  </div>
                )}
              </>
            );
          }}
        />
      </div>
    );
  }
}

UPStripeCardForm.displayName = 'UPStripeCardForm';

export default UPStripeCardForm;
